import { Flex, notification } from 'antd';
import { FC } from 'react';
import { useDispatch } from 'react-redux';
import { usePermissions } from '@/hooks/usePermissions';
import type { Study } from '@/models/study';
import { Tag } from '@/shared/ui/Tag';
import { formatTagLabel } from '@/shared/utils/tagLabel';
import {
  useLazyGetReportQuery,
  useUpdateReportMutation,
} from '@/state/slices/reports';
import {
  restoreWorklistTag,
  setWorklistTags,
  StudiesAPI,
} from '@/state/slices/studies';
import type { AppDispatch } from '@/state/store';
import styles from './StudyTagsCell.module.css';

interface Props {
  study: Study;
  compact?: boolean;
}

// Removals for one report are saved one after another, so an older save can
// never land after a newer one. `pending` holds the tags removed but not saved
// yet, so every save leaves all of them out.
const queues = new Map<string, { last: Promise<void>; pending: Set<string> }>();

/**
 * Worklist tags column. The tags are the latest report's, so removing one
 * saves the report without it. Same rule as editing the report in the Reader:
 * only roles that can edit, and never on a FINAL report.
 *
 * The tag leaves the row right away and the save runs in the background, with
 * no list reload. The save reads the report's tags fresh first, so a tag added
 * since the list loaded is kept.
 */
const StudyTagsCell: FC<Props> = ({ study, compact = false }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [fetchReport] = useLazyGetReportQuery();
  const [updateReport] = useUpdateReportMutation();
  const { canEdit } = usePermissions({
    isFinalized: study.reportStatus?.toUpperCase() === 'FINAL',
  });

  if (!study.tags || study.tags.length === 0) return <>—</>;

  const { reportUuid } = study;
  const canRemove = !!reportUuid && canEdit;

  const save = async (uuid: string, tag: string, pending: Set<string>) => {
    try {
      const report = await fetchReport(uuid).unwrap();
      await updateReport({
        uuid,
        tags: report.tags.filter((t) => !pending.has(t)),
      }).unwrap();
    } catch {
      notification.error({
        message: `Could not remove ${formatTagLabel(tag)}`,
        placement: 'bottomRight',
      });
      // Show this tag again (only this one: other removals may have saved),
      // then reload the row to match the server.
      dispatch(restoreWorklistTag(uuid, tag));
      dispatch(
        StudiesAPI.util.invalidateTags([{ type: 'Studies', id: study.uuid }])
      );
    } finally {
      pending.delete(tag);
      if (pending.size === 0) queues.delete(uuid);
    }
  };

  const handleRemove = (tag: string) => {
    if (!reportUuid) return;
    dispatch(
      setWorklistTags(
        reportUuid,
        study.tags.filter((t) => t !== tag)
      )
    );
    const queue = queues.get(reportUuid) ?? {
      last: Promise.resolve(),
      pending: new Set<string>(),
    };
    queues.set(reportUuid, queue);
    queue.pending.add(tag);
    queue.last = queue.last.then(() => save(reportUuid, tag, queue.pending));
  };

  return (
    <Flex gap={4} wrap className={styles.tags}>
      {study.tags.map((tag) => (
        <Tag
          key={tag}
          value={tag}
          compact={compact}
          ai={study.aiTags?.includes(tag)}
          onRemove={canRemove ? handleRemove : undefined}
          removeDataTestId={`remove-tag-${tag}`}
        />
      ))}
    </Flex>
  );
};

export default StudyTagsCell;
